import { createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { feeScheduleApi } from "marketplace/teams/fee-schedules/_api/fee-schedule-api";
import { FeeScheduleJSON } from "marketplace/teams/fee-schedules/_schemas/fee-schedule-schema";
import MarketplaceTeam from "marketplace/teams/team";

export type FeeScheduleStoreState = {
  byTeamId: {
    entries: Record<string, FeeScheduleJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

const initialState: FeeScheduleStoreState = {
  byTeamId: {
    entries: {},
    loading: {},
    error: {},
  },
};

export const feeSchedulesSlice = createSlice({
  name: "feeSchedules",
  initialState,
  reducers: {
    addFeeSchedule: (
      state,
      action: PayloadAction<{ team: MarketplaceTeam; feeSchedule: FeeScheduleJSON }>
    ) => {
      if (!action.payload.team.id) {
        return;
      }
      state.byTeamId.entries[action.payload.team.id.value].push(
        action.payload.feeSchedule
      );
    },
    removeFeeSchedule: (
      state,
      action: PayloadAction<{ team: MarketplaceTeam; feeSchedule: FeeScheduleJSON }>
    ) => {
      if (!action.payload.team.id) {
        return;
      }
      state.byTeamId.entries[action.payload.team.id.value] =
        state.byTeamId.entries[action.payload.team.id.value].filter(
          (feeSchedule) => feeSchedule.id !== action.payload.feeSchedule.id
        );
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(feeScheduleApi.endpoints.getFeeSchedulesForTeam.matchPending, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.byTeamId.loading[teamId] = true;
      state.byTeamId.error[teamId] = null;
    });
    builder.addMatcher(feeScheduleApi.endpoints.getFeeSchedulesForTeam.matchFulfilled, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.byTeamId.entries[teamId] = action.payload;
      state.byTeamId.loading[teamId] = false;
    });
    builder.addMatcher(feeScheduleApi.endpoints.getFeeSchedulesForTeam.matchRejected, (state, action) => {
      const teamId = action.meta.arg.originalArgs.teamId;
      state.byTeamId.loading[teamId] = false;
      state.byTeamId.error[teamId] = action.error;
    });
  },
});

export const getFeeSchedulesForTeam = (teamId: Guid) => (state: RootState) =>
  state.feeSchedules.byTeamId.entries[teamId.value];
export const getIsLoadingFeeSchedulesForTeam = (teamId: Guid) => (state: RootState) =>
  state.feeSchedules.byTeamId.loading[teamId.value];
export const getErrorLoadingFeeSchedulesForTeam = (teamId: Guid) => (state: RootState) =>
  state.feeSchedules.byTeamId.error[teamId.value];
